import {
  Combobox as C,
  ComboboxInput,
  ComboboxOption,
  ComboboxOptions,
  Label,
} from "@headlessui/react";
import React, { useState, useRef } from "react";

export interface ComboboxOptionI {
  value: string;
  label: string;
  className?: string;
}

interface ComboboxProps {
  options: ComboboxOptionI[];
  allOptions?: ComboboxOptionI[];
  label?: string;
  value?: ComboboxOptionI;
  onChange: (value: ComboboxOptionI) => void;
  placeholder?: string;
  disabled?: boolean;
  onCreateOption?: (inputValue: string) => void;
}

export default function Combobox({
  options,
  allOptions,
  label,
  value,
  onChange,
  placeholder = "Select an option...",
  disabled = false,
  onCreateOption,
}: ComboboxProps) {
  const [query, setQuery] = useState("");
  const comboboxOption = useRef(null);

  const filteredOptions =
    query === ""
      ? options
      : options.filter((option) =>
          option.label.toLowerCase().includes(query.trim().toLowerCase()),
        );

  const showCreateOption =
    query &&
    !(allOptions || options).some(
      (option) => option.label.toLowerCase() === query.trim().toLowerCase(),
    ) &&
    onCreateOption;

  const handleChange = (option: ComboboxOptionI | null) => {
    if (!option) return;
    if (showCreateOption && option.label === query && option.value === query) {
      onCreateOption(query.trim());
      setQuery("");
      return;
    }
    onChange(option);
    setTimeout(() => {
      comboboxOption?.current?.blur();
    }, 0);
  };

  return (
    <C immediate value={value} onChange={handleChange}>
      <div className="relative w-full">
        {label && <Label>{label}</Label>}
        <ComboboxInput
          aria-label="Assignee"
          displayValue={(option: ComboboxOptionI) => option?.label}
          onChange={(event) => setQuery(event.target.value)}
          placeholder={placeholder}
          className="w-full px-4 py-2 border border-[#0F172A] rounded-md focus:outline-none focus:border-[#00A3E0] disabled:bg-gray-200 disabled:text-gray-500 disabled:cursor-not-allowed"
          disabled={disabled}
          autoComplete="off"
          ref={comboboxOption}
        />
        <ComboboxOptions className="absolute mt-1 w-full bg-white shadow-lg max-h-60 rounded-md py-1 text-base ring-1 ring-black ring-opacity-5 overflow-auto focus:outline-none sm:text-sm z-50">
          {showCreateOption && (
            <ComboboxOption
              value={{ label: query, value: query }}
              className={({ active }) =>
                `relative cursor-default select-none py-2 pl-10 pr-4 ${active ? "bg-blue-100" : ""}`
              }
            >
              Create &quot;{query}&quot;
            </ComboboxOption>
          )}
          {filteredOptions.map((option) => (
            <ComboboxOption
              key={option.value}
              value={option}
              className={({ active }) =>
                `relative cursor-default select-none py-2 pl-10 pr-4 ${
                  active ? "bg-blue-100" : ""
                }`
              }
            >
              {({ selected }) => (
                <>
                  <span
                    className={`block truncate ${
                      selected ? "font-medium" : "font-normal"
                    } ${option.className}`}
                  >
                    {option.label}
                  </span>
                </>
              )}
            </ComboboxOption>
          ))}
        </ComboboxOptions>
      </div>
    </C>
  );
}
